import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ShopifyBuyButton from '../components/ShopifyBuyButton';

// Shopify configuration for fetching product images
const SHOPIFY_CONFIG = {
  domain: 'x16ha3-n1.myshopify.com',
  storefrontAccessToken: 'f1f5374c07806735e6632074755592e5',
};

const TheLighter = () => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch product images from Shopify
  useEffect(() => {
    const fetchProductImages = async () => {
      const query = `
        query getProduct($id: ID!) {
          node(id: $id) {
            ... on Product {
              images(first: 10) {
                edges {
                  node {
                    url
                  }
                }
              }
            }
          }
        }
      `;

      try {
        const response = await fetch(
          `https://${SHOPIFY_CONFIG.domain}/api/2024-01/graphql.json`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontAccessToken,
            },
            body: JSON.stringify({
              query,
              variables: { id: 'gid://shopify/Product/9162088808702' },
            }),
          }
        );

        const data = await response.json();
        const node = data.data?.node;
        
        if (node) {
          const productImages = node.images?.edges?.map(edge => edge.node.url) || [];
          setImages(productImages);
        }
      } catch (err) {
        console.error('Error fetching product images:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductImages();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-plumo-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
          <div className="animate-pulse">
            <div className="h-6 bg-plumo-bone w-24 mb-12" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
              <div className="aspect-[4/5] bg-plumo-bone" />
              <div className="space-y-6">
                <div className="h-10 bg-plumo-bone w-3/4" />
                <div className="h-6 bg-plumo-bone w-1/4" />
                <div className="h-20 bg-plumo-bone" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-plumo-white" data-testid="product-page-lighter">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        {/* Back Link */}
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 font-body text-[12px] tracking-[0.1em] text-plumo-black/50 hover:text-plumo-black transition-colors mb-12"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="w-4 h-4" />
          back to shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Images */}
          <div className="lg:sticky lg:top-32 lg:self-start">
            {/* Main Image */}
            <div className="aspect-[4/5] bg-plumo-bone overflow-hidden mb-4">
              {images.length > 0 && (
                <img
                  src={images[selectedImage]}
                  alt="The Lighter"
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              )}
            </div>

            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <div className="flex gap-3">
                {images.slice(0, 4).map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-16 h-20 overflow-hidden transition-opacity ${
                      selectedImage === index ? 'opacity-100' : 'opacity-50 hover:opacity-75'
                    }`}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img
                      src={img}
                      alt={`The Lighter view ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="lg:pt-8">
            {/* Title */}
            <h1 
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 500,
                fontStyle: 'italic',
                fontSize: 'clamp(32px, 4vw, 48px)',
                color: '#1A1814',
                lineHeight: 1.1,
              }}
              data-testid="product-name"
            >
              The Lighter
            </h1>

            {/* Price - Sale format */}
            <div 
              className="mt-4"
              data-testid="product-price"
              style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
            >
              <span 
                style={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: '16px',
                  color: 'rgba(26,24,20,0.4)',
                  textDecoration: 'line-through',
                }}
              >
                $25
              </span>
              <span 
                style={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: '16px',
                  fontWeight: 500,
                  color: '#C9A961',
                }}
              >
                $14
              </span>
            </div>

            {/* Description */}
            <div 
              className="mt-8"
              style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: '13px',
                color: 'rgba(26,24,20,0.6)',
                lineHeight: 1.7,
              }}
              data-testid="product-description"
            >
              <p className="mb-4">
                gold. heavy. yours.
              </p>
              <p className="mb-4">
                butane refillable. solid brass with 18k gold plating. adjustable flame. the kind of lighter you don't lose.
              </p>
              <p>
                size: 6cm × 3cm × 1.5cm. weight: 85g.
              </p>
            </div>

            {/* Category */}
            <p 
              className="mt-6"
              style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: '11px',
                letterSpacing: '0.1em',
                color: 'rgba(26,24,20,0.4)',
              }}
            >
              category: <span style={{ color: '#C9A961' }}>accessories</span>
            </p>

            {/* Divider */}
            <div className="border-t border-plumo-black/10 my-8" />

            {/* Shopify Buy Button */}
            <ShopifyBuyButton productId="lighter" />

            {/* Product Details */}
            <div className="mt-12 space-y-3">
              <p 
                style={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: '12px',
                  color: 'rgba(26,24,20,0.5)',
                }}
              >
                · solid brass construction
              </p>
              <p 
                style={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: '12px',
                  color: 'rgba(26,24,20,0.5)',
                }}
              >
                · 18k gold plating
              </p>
              <p 
                style={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: '12px',
                  color: 'rgba(26,24,20,0.5)',
                }}
              >
                · free shipping on orders over $50
              </p>
            </div>
          </div>
        </div>

        {/* Additional Images */}
        {images.length > 1 && (
          <div className="mt-20 space-y-8">
            {images.slice(1, 4).map((img, index) => (
              <div key={index} className="max-w-3xl mx-auto">
                <img
                  src={img}
                  alt={`The Lighter detail ${index + 2}`}
                  className="w-full h-auto"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TheLighter;
